import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogIcon,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Pencil,
  Clock,
  WarningCircle,
  Trash,
  Newspaper,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/ui/status-badge";
import { blogCardExcerpt } from "@/lib/blog-excerpt";
import type { TherapistBlogPost } from "@/types";

export function TherapistBlogEmptyState() {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
        <Newspaper weight="duotone" className="size-6 text-primary" />
      </div>
      <h3
        className="mt-5 text-base font-semibold text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        Još nemate članaka
      </h3>
      <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-muted-foreground">
        Napišite svoj prvi članak i podijelite stručno znanje sa zajednicom Psihotrona.
      </p>
      <Button className="mt-6" asChild>
        <Link to="/dashboard/therapist/blog/new">
          <Plus className="size-4" weight="bold" />
          Napiši prvi članak
        </Link>
      </Button>
    </div>
  );
}

export function TherapistPostCard({
  post,
  onDelete,
  deleting,
}: {
  post: TherapistBlogPost;
  onDelete: () => void;
  deleting: boolean;
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const excerpt = blogCardExcerpt(post);
  return (
    <div className="group border-b border-border/60 py-5 first:pt-0.5 last:border-b-0 last:pb-0.5">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        {/* Cover */}
        <div className="relative shrink-0 overflow-hidden rounded-xl bg-muted/40 sm:w-44">
          <div className="aspect-[16/10] sm:aspect-[4/3]">
            {post.coverImageUrl ? (
              <img
                src={post.coverImageUrl}
                alt=""
                className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              />
            ) : (
              <div className="flex size-full items-center justify-center">
                <ImageIcon className="size-8 text-muted-foreground/30" weight="duotone" />
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <StatusBadge status={post.status} />
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock weight="duotone" className="size-3.5" />
                  {formatDate(post.updatedAt || post.createdAt)}
                </span>
              </div>
              <h3
                className="text-lg font-semibold leading-snug tracking-tight text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {post.title}
              </h3>
              {excerpt && (
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                  {excerpt}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex shrink-0 items-center gap-1">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/dashboard/therapist/blog/${post.id}/edit`}>
                  <Pencil className="size-3.5" />
                  Uredi
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                disabled={deleting}
                title="Obriši"
              >
                <Trash className="size-3.5 text-destructive" />
              </Button>
            </div>
          </div>

          {/* Rejection reason — opens in dialog */}
          {post.status === "rejected" && post.rejectionReason && (
            <button
              type="button"
              onClick={() => setReasonOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-1.5 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              <WarningCircle weight="fill" className="size-3.5 text-rose-600" />
              Razlog odbijanja
            </button>
          )}
        </div>
      </div>

      {/* Rejection dialog */}
      {post.status === "rejected" && post.rejectionReason && (
        <Dialog open={reasonOpen} onClose={() => setReasonOpen(false)} className="sm:max-w-md">
          <DialogHeader onClose={() => setReasonOpen(false)}>
            <div className="flex items-center gap-3">
              <DialogIcon tone="destructive">
                <WarningCircle weight="fill" className="size-5" />
              </DialogIcon>
              <DialogTitle>Razlog odbijanja</DialogTitle>
            </div>
          </DialogHeader>
          <DialogBody>
            <p className="mb-2 text-sm font-medium text-muted-foreground">
              Članak: <span className="text-foreground">{post.title}</span>
            </p>
            <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
              <p className="text-sm leading-relaxed text-rose-900 whitespace-pre-wrap">
                {post.rejectionReason}
              </p>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              Možete urediti članak prema napomenama i ponovo ga poslati na pregled.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonOpen(false)}>
              Zatvori
            </Button>
            <Button asChild>
              <Link to={`/dashboard/therapist/blog/${post.id}/edit`}>
                <Pencil className="size-3.5" />
                Uredi članak
              </Link>
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
}
